import type { OfferingType } from '../types/lesson';

export type LessonEvent = 'submitted' | 'delivered';

export interface LessonEventLabelOpts {
  event: LessonEvent;
  /**
   * Order's offering type. NOTE: lessons themselves don't carry the type —
   * the discriminator lives on the parent order doc. Callers must
   * resolve `order.type` and pass it here. When undefined, the helper
   * falls back to a generic label so partial data still renders sanely.
   */
  offeringType?: OfferingType;
  /** 1-based position within a training program. Required for `training_program`. */
  lessonNumber?: number;
  /** Total lessons in the program. Required for `training_program`. */
  totalLessons?: number;
}

/**
 * Single source of truth for the system-event card label on lesson_request
 * (player video submitted) and lesson_delivered bubbles. Branches by
 * offering type so the chat reads as the right product:
 *
 *   shot_consult     → "Shot consult submitted" / "Shot consult delivered"
 *   single_lesson    → "Single async lesson submitted" / "Single async lesson delivered"
 *   training_program → "Training program · Lesson N of M submitted" / "... delivered"
 *
 * Uses the middle dot (·, U+00B7) separator for the training program case.
 */
export function lessonEventLabel(opts: LessonEventLabelOpts): string {
  const { event, offeringType, lessonNumber, totalLessons } = opts;
  const verb = event === 'submitted' ? 'submitted' : 'delivered';

  if (offeringType === 'shot_consult') {
    return `Shot consult ${verb}`;
  }
  if (offeringType === 'single_lesson') {
    return `Single async lesson ${verb}`;
  }
  if (offeringType === 'training_program' && lessonNumber && totalLessons) {
    return `Training program · Lesson ${lessonNumber} of ${totalLessons} ${verb}`;
  }
  // Fallback when offering type can't be resolved (shouldn't happen in
  // practice — order data is available alongside every lesson — but
  // keeps the bubble readable rather than rendering "undefined").
  return event === 'submitted' ? 'Video submitted' : 'Lesson delivered';
}
